import { push } from 'connected-react-router';

import urls from '../urls';
import apiUrls from '../../youwell-common/apiUrls';
import Auth from '../../youwell-common/Auth';
import ReduxStoreHelper, { LOGOUT, CLEAR_ERROR } from '../../youwell-common/ReduxStoreHelper';
import { loginLevels, logoutReasons } from '../../youwell-common/constants';
import { sortByString } from '../../youwell-common/arrayUtils';

const ACTION_PREFIX = 'AUTH';

const storeHelper = new ReduxStoreHelper(ACTION_PREFIX);

const SUCCESS_LOGIN = 'SUCCESS_LOGIN';
const SUCCESS_GETPROFILE = 'SUCCESS_GETPROFILE';
const SUCCESS_SAVEPROFILE = 'SUCCESS_SAVEPROFILE';
const RESET_PASSWORD = 'RESET_PASSWORD';
const FORGOT_PASSWORD = 'FORGOT_PASSWORD';
const SUCCESS_REGISTER = 'SUCCESS_REGISTER';
const ORGANIZATIONS_RECEIVED = 'ORGANIZATIONS_RECEIVED';
const SUCCESS_DELETE_TRUSTED_DEVICES = 'SUCCESS_DELETE_TRUSTED_DEVICES';
const SUCCESS_GET_TRUSTED_DEVICES = 'SUCCESS_GET_TRUSTED_DEVICES';
const CANCEL_TWOFACTOR = 'CANCEL_TWOFACTOR';
const UPDATE_USERCONFIG = 'UPDATE_USERCONFIG';
const TOKEN_EXPIRED = 'TOKEN_EXPIRED';

export const actionCreators = {
    clearError: () => (dispatch) => {
        dispatch({
            type: CLEAR_ERROR,
        });
    },
    loginWithCode: (code, provider, state, options) => (dispatch) => {
        storeHelper.fetch(dispatch, {
            url: `${apiUrls.portal.auth}/codelogin`,
            body: { code, state, provider },
            method: 'POST',
            completeAction: `${ACTION_PREFIX}/${SUCCESS_LOGIN}`,
            onData: (data) => {
                Auth.setLoginInfo(data.loginLevel);
            },
            options,
        });
    },
    login: (email, password, options) => (dispatch) => {
        storeHelper.fetch(dispatch, {
            url: `${apiUrls.portal.auth}/login`,
            body: { email, password },
            method: 'POST',
            completeAction: `${ACTION_PREFIX}/${SUCCESS_LOGIN}`,
            onData: (data) => {
                Auth.setLoginInfo(data.loginLevel);
            },
            options,
        });
    },
    loginWithPin: (pin, id, remember, options) => (dispatch) => {
        storeHelper.fetch(dispatch, {
            url: `${apiUrls.portal.auth}/login2fa`,
            body: {
                pin, id, remember,
            },
            method: 'POST',
            completeAction: `${ACTION_PREFIX}/${SUCCESS_LOGIN}`,
            onData: (data) => {
                Auth.setLoginInfo(data.loginLevel);
            },
            options,
        });
    },
    cancelTwoFactor: () => (dispatch) => {
        dispatch({ type: `${ACTION_PREFIX}/${CANCEL_TWOFACTOR}` });
    },
    logoutRole: options => (dispatch) => {
        storeHelper.fetch(dispatch, {
            url: `${apiUrls.portal.auth}/logoutrole`,
            method: 'POST',
            onData: (data) => {
                dispatch({ type: LOGOUT, data, logoutReason: logoutReasons.changeRole }); // Send Logout to empty all other state
                if (data && data.loginLevel) {
                    Auth.setLoginInfo(data.loginLevel);
                } else {
                    Auth.deauthenticateUser();
                }
            },
            options,
        });
    },
    selectRole: (roleId, options) => (dispatch) => {
        storeHelper.fetch(dispatch, {
            url: `${apiUrls.portal.auth}/role/${roleId}`,
            method: 'POST',
            completeAction: `${ACTION_PREFIX}/${SUCCESS_LOGIN}`,
            onData: (data) => {
                Auth.setLoginInfo(data.loginLevel);
            },
            options,
        });
    },
    logout: (logoutReason, redirectTo) => (dispatch) => {
        const loginLevel = Auth.getLoginLevel();
        const redirect = redirectTo || (loginLevel !== loginLevels.level4 ? urls.home : null);
        const redirectExternal = loginLevel === loginLevels.level4 ? `${apiUrls.baseUrl}/${apiUrls.route.oauth}/logout/idporten` : null;

        storeHelper.fetch(dispatch, {
            url: `${apiUrls.portal.auth}/logout`,
            method: 'POST',
            onData: () => {
                dispatch({ type: LOGOUT, logoutReason });
                Auth.deauthenticateUser();
            },
            options: {
                redirect,
                redirectExternal,
            },
            onError: () => {
                dispatch({ type: LOGOUT, logoutReason });
                Auth.deauthenticateUser();

                if (redirect) {
                    dispatch(push(redirect));
                } else if (redirectExternal) {
                    window.location = redirectExternal;
                }
            },
        });
    },
    getUserProfile: options => (dispatch) => {
        storeHelper.fetch(dispatch, {
            url: `${apiUrls.portal.auth}/profile`,
            completeAction: `${ACTION_PREFIX}/${SUCCESS_GETPROFILE}`,
            onUnauthorized: () => {
                dispatch({ type: `${ACTION_PREFIX}/${TOKEN_EXPIRED}` });
                dispatch(push(urls.login));
            },
            options,
        });
    },
    saveUserProfile: (elementToSave, options) => (dispatch) => {
        storeHelper.fetch(dispatch, {
            url: `${apiUrls.portal.auth}/profile`,
            body: elementToSave,
            method: 'PUT',
            completeAction: `${ACTION_PREFIX}/${SUCCESS_SAVEPROFILE}`,
            options,
        });
    },
    registerUser: (userData, options) => (dispatch) => {
        storeHelper.fetch(dispatch, {
            url: `${apiUrls.portal.registration}`,
            body: userData,
            method: 'POST',
            completeAction: `${ACTION_PREFIX}/${SUCCESS_REGISTER}`,
            options,
        });
    },
    registerContactUser: (userData, options) => (dispatch) => {
        storeHelper.fetch(dispatch, {
            url: `${apiUrls.portal.registration}/contact`,
            body: userData,
            method: 'POST',
            completeAction: `${ACTION_PREFIX}/${SUCCESS_REGISTER}`,
            options,
        });
    },
    registerOrganization: (orgData, options) => (dispatch) => {
        storeHelper.fetch(dispatch, {
            url: `${apiUrls.portal.auth}/organization`,
            body: orgData,
            method: 'POST',
            completeAction: `${ACTION_PREFIX}/${SUCCESS_LOGIN}`,
            onData: (data) => {
                Auth.setLoginInfo(data.loginLevel);
            },
            options,
        });
    },
    getAllPublicOrganizations: options => (dispatch) => {
        storeHelper.fetch(dispatch, {
            url: `${apiUrls.portal.registration}/organization`,
            method: 'GET',
            completeAction: `${ACTION_PREFIX}/${ORGANIZATIONS_RECEIVED}`,
            options,
        });
    },
    selectOrganization: (orgId, options) => (dispatch) => {
        storeHelper.fetch(dispatch, {
            url: `${apiUrls.portal.auth}/organization/${orgId}`,
            method: 'POST',
            completeAction: `${ACTION_PREFIX}/${SUCCESS_LOGIN}`,
            onData: (data) => {
                Auth.setLoginInfo(data.loginLevel);
            },
            options,
        });
    },
    forgotPassword: (email, options) => (dispatch) => {
        storeHelper.fetch(dispatch, {
            url: `${apiUrls.portal.auth}/forgotPassword`,
            method: 'POST',
            requestId: `${ACTION_PREFIX}/${FORGOT_PASSWORD}`,
            body: {
                email,
            },
            options,
        });
    },
    resetPassword: (body, options) => (dispatch) => {
        storeHelper.fetch(dispatch, {
            url: `${apiUrls.portal.auth}/resetPassword`,
            method: 'POST',
            requestId: `${ACTION_PREFIX}/${RESET_PASSWORD}`,
            body,
            options,
        });
    },
    removeAllTrustedDevices: (body, options) => (dispatch) => {
        storeHelper.fetch(dispatch, {
            url: `${apiUrls.portal.user}/trusteddevices`,
            body,
            method: 'DELETE',
            completeAction: `${ACTION_PREFIX}/${SUCCESS_DELETE_TRUSTED_DEVICES}`,
            options,
        });
    },
    getAllTrustedDevicesForUser: options => (dispatch) => {
        storeHelper.fetch(dispatch, {
            url: `${apiUrls.portal.user}/trusteddevices`,
            method: 'GET',
            completeAction: `${ACTION_PREFIX}/${SUCCESS_GET_TRUSTED_DEVICES}`,
            options,
        });
    },
    setConfig: storeHelper.createInsertActionCreator(`${apiUrls.portal.user}/setc`, UPDATE_USERCONFIG),
    redirect: url => (dispatch) => {
        dispatch(push(url));
    },
};

const initialState = {
    ...storeHelper.defaultInitialState,
    profileData: null,
    clientConfig: {},
    logoutReason: null,
    twoFactorId: '',
    trustedDevices: [],
};

export const reducer = (state, action) => {
    // Base reducer overloads
    switch (action.type) {
    case LOGOUT: {
        // In case of changerole (logout-with reduced token)
        return {
            ...initialState,
            profileData: action.data?.profile,
            clientConfig: action.data?.profile?.clientConfig || {},
            logoutReason: action.logoutReason,
        };
    }
    default: {
        const stateFromDefaultActions = storeHelper.defaultReducer(state, action, initialState);

        if (stateFromDefaultActions !== null) {
            return stateFromDefaultActions;
        }
    }
    }

    switch (action.type) {
    case `${ACTION_PREFIX}/${TOKEN_EXPIRED}`: {
        Auth.deauthenticateUser();
        return {
            ...initialState,
            logoutReason: logoutReasons.tokenExpired,
        };
    }
    case `${ACTION_PREFIX}/${SUCCESS_LOGIN}`: {
        return {
            ...state,
            twoFactorId: action.data.twoFactorId,
            profileData: action.data.profile,
            clientConfig: action.data?.profile?.clientConfig || {},
            logoutReason: null,
        };
    }
    case `${ACTION_PREFIX}/${CANCEL_TWOFACTOR}`: {
        return {
            ...state,
            twoFactorId: null,
            errors: [],
        };
    }
    case `${ACTION_PREFIX}/${SUCCESS_GETPROFILE}`: {
        return {
            ...state,
            profileData: action.data.profile,
            clientConfig: action.data.profile?.clientConfig || {},
        };
    }
    case `${ACTION_PREFIX}/${SUCCESS_SAVEPROFILE}`: {
        return {
            ...state,
            profileData: { ...state.profileData, ...action.data.profile },
        };
    }
    case `${ACTION_PREFIX}/${SUCCESS_REGISTER}`: {
        if (action.data.createdOrg) {
            return {
                ...state,
                organizations: sortByString([...state.organizations, action.data.createdOrg]),
            };
        }

        return {
            ...state,
        };
    }
    case `${ACTION_PREFIX}/${ORGANIZATIONS_RECEIVED}`: {
        return {
            ...state,
            organizations: sortByString(action.data),
        };
    }
    case `${ACTION_PREFIX}/${SUCCESS_DELETE_TRUSTED_DEVICES}`: {
        return {
            ...state,
            trustedDevices: [],
        };
    }
    case `${ACTION_PREFIX}/${SUCCESS_GET_TRUSTED_DEVICES}`: {
        return {
            ...state,
            trustedDevices: action.data,
        };
    }
    case `${ACTION_PREFIX}/${UPDATE_USERCONFIG}`: {
        return {
            ...state,
            profileData: {
                ...state.profileData,
                userSettings: {
                    ...state.profileData?.userSettings,
                    [action.inputData.key]: action.inputData.value,
                },
            },
        };
    }

    default: {
        return state || initialState;
    }
    }
};
